import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { List, ChevronLeft, Copy } from 'lucide-react';
import styles from '../WikiPage.module.css';

const WIDE_VIEWPORT = 1300;

const WikiTableOfContents = ({ content }) => {
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [collapsed, setCollapsed] = useState(true);
  const [isNarrow, setIsNarrow] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const copyLinkToHeading = useCallback((e, headingId) => {
    e.stopPropagation();
    const url = typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}#${headingId}`
      : '';
    if (!url) return;
    navigator.clipboard?.writeText(url).then(() => setCopiedId(headingId));
  }, []);

  useEffect(() => {
    if (copiedId === null) return;
    const t = setTimeout(() => setCopiedId(null), 2000);
    return () => clearTimeout(t);
  }, [copiedId]);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${WIDE_VIEWPORT}px)`);
    const update = () => {
      const narrow = mq.matches;
      setIsNarrow(narrow);
      setCollapsed(true);
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!content) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const headingElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const extractedHeadings = Array.from(headingElements).map((heading, index) => {
      const id = `heading-${index}`;
      const level = parseInt(heading.tagName.substring(1), 10);
      const text = heading.textContent || '';
      return { id, level, text };
    });
    setHeadings(extractedHeadings);
    const t = setTimeout(() => {
      const domHeadings = document.querySelectorAll(
        '[data-wiki-article] h1, [data-wiki-article] h2, [data-wiki-article] h3, [data-wiki-article] h4, [data-wiki-article] h5, [data-wiki-article] h6'
      );
      domHeadings.forEach((heading, index) => {
        heading.id = `heading-${index}`;
      });
    }, 100);
    return () => clearTimeout(t);
  }, [content]);

  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings
        .map((h) => document.getElementById(h.id))
        .filter(Boolean);
      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i];
        const rect = element.getBoundingClientRect();
        if (rect.top <= 150) {
          setActiveId(element.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const scrollY = window.scrollY ?? window.pageYOffset;
      const targetY = element.getBoundingClientRect().top + scrollY - 100;
      window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
    }
    if (isNarrow) setCollapsed(true);
  };

  if (headings.length === 0) return null;

  if (isNarrow) {
    return (
      <>
        {!collapsed && (
          <div
            className={styles.tocOverlay}
            onClick={() => setCollapsed(true)}
            aria-hidden
          />
        )}
        <button
          type="button"
          className={styles.tocTrigger}
          onClick={() => setCollapsed((c) => !c)}
          title="Содержание"
          aria-label={collapsed ? 'Открыть содержание' : 'Закрыть содержание'}
          aria-expanded={!collapsed}
        >
          <ChevronLeft size={24} className={styles.tocTriggerIcon} />
        </button>
        <aside
          className={`${styles.tableOfContents} ${styles.tocPanel} ${!collapsed ? styles.tocPanelOpen : ''}`}
          aria-hidden={collapsed}
        >
          <div className={styles.tocPanelHeader}>
            <List size={20} />
            <span>Содержание</span>
          </div>
          <nav id="wiki-toc-nav" className={styles.tocNav}>
            {headings.map((heading) => (
              <div key={heading.id} className={styles.tocItemWrap}>
                <button
                  type="button"
                  className={`${styles.tocItem} ${styles[`tocLevel${heading.level}`] || ''} ${
                    activeId === heading.id ? styles.tocItemActive : ''
                  }`}
                  onClick={() => scrollToHeading(heading.id)}
                >
                  {heading.text}
                </button>
                <button
                  type="button"
                  className={styles.tocItemCopy}
                  onClick={(e) => copyLinkToHeading(e, heading.id)}
                  title="Копировать ссылку на раздел"
                  aria-label="Копировать ссылку на раздел"
                >
                  <Copy size={14} />
                </button>
              </div>
            ))}
          </nav>
        </aside>
        {copiedId && createPortal(
          <div className={styles.tocCopyToast} role="status" aria-live="polite">
            Ссылка скопирована
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      <div className={styles.tocSpacer} aria-hidden />
      <aside className={styles.tableOfContents}>
        <div className={styles.tocHeader}>
          <List size={18} />
          <span>Содержание</span>
        </div>
        <nav id="wiki-toc-nav" className={styles.tocNav}>
          {headings.map((heading) => (
            <div key={heading.id} className={styles.tocItemWrap}>
              <button
                type="button"
                className={`${styles.tocItem} ${styles[`tocLevel${heading.level}`] || ''} ${
                  activeId === heading.id ? styles.tocItemActive : ''
                }`}
                onClick={() => scrollToHeading(heading.id)}
              >
                {heading.text}
              </button>
              <button
                type="button"
                className={styles.tocItemCopy}
                onClick={(e) => copyLinkToHeading(e, heading.id)}
                title="Копировать ссылку на раздел"
                aria-label="Копировать ссылку на раздел"
              >
                <Copy size={14} />
              </button>
            </div>
          ))}
        </nav>
      </aside>
      {copiedId && createPortal(
        <div className={styles.tocCopyToast} role="status" aria-live="polite">
          Ссылка скопирована
        </div>,
        document.body
      )}
    </>
  );
};

export default WikiTableOfContents;
